import React, { useState, useEffect } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import ErrorMsg from "./msg/ErrorMsg";
import SuccsMsg from "./msg/SuccsMsg";
import {
    Box, CircularProgress, Dialog, Grid, styled, Table, TableBody, TableCell, tableCellClasses,
    TableContainer, TableHead, TableRow, TextField, Button, Paper, Typography, Checkbox,
    Chip, IconButton, Tooltip, Card, CardContent, Tabs, Tab,
    FormControl, InputLabel, Select, MenuItem, DialogTitle,
    DialogContent, DialogActions
} from "@mui/material";
import Appbar from "./Appbar";
import MenuList from "./MenuList";
import SendIcon from '@mui/icons-material/Send';
import MessageIcon from '@mui/icons-material/Message';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

// استایل برای سلول‌های جدول
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

// کارت نمایش موجودی
const BalanceCard = styled(Card)(({ theme }) => ({
    backgroundColor: '#4caf50',
    color: 'white',
    marginBottom: theme.spacing(3),
    borderRadius: 10,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
}));

// تابع برای نمایش اعداد با جداکننده هزارگان
const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    const number = typeof num === 'string' ? parseFloat(num.replace(/,/g, '')) : num;
    if (isNaN(number)) return '0';
    return number.toLocaleString('fa-IR');
};

const SendSMS = () => {
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [customerList, setCustomerList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [message, setMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [succsMsg, setSuccsMsg] = useState("");
    const [balance, setBalance] = useState(null);
    const [balanceDetails, setBalanceDetails] = useState(null); // این خط اضافه شده
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // State برای مدیریت قالب‌ها
    const [templates, setTemplates] = useState([]);
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState({ id: 0, title: '', content: '' });
    const [selectedTemplate, setSelectedTemplate] = useState("");

    // دریافت لیست مشتریان
    useEffect(() => {
        const fetchCustomers = async () => {
            setPageLoading(true);
            try {
                const data = {
                    token: localStorage.getItem("adminlogintoken"),
                };
                const response = await axios.post("/api/get_customerlist.php", data);

                if (response.status === 200) {
                    if (response.data.error === false) {
                        setCustomerList(response.data.data);
                        setFilteredList(response.data.data);
                    } else {
                        localStorage.clear();
                    }
                }
            } catch (error) {
                console.error("Error fetching customers:", error);
                setErrorMsg("خطا در دریافت لیست مشتریان");
            } finally {
                setPageLoading(false);
            }
        };

        if (localStorage.getItem("adminlogintoken")) {
            fetchCustomers();
            checkBalance();
            fetchTemplates();
        }
    }, []);

    // دریافت قالب‌های پیامک
    const fetchTemplates = async () => {
        try {
            const response = await axios.post("/api/get_sms_templates.php", {
                token: localStorage.getItem("adminlogintoken")
            });
            if (response.data.error === false) {
                setTemplates(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
        }
    };

    // فیلتر کردن مشتریان بر اساس جستجو
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredList(customerList);
        } else {
            const filtered = customerList.filter(customer =>
                customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.phone?.includes(searchTerm)
            );
            setFilteredList(filtered);
        }
    }, [searchTerm, customerList]);

    // بررسی موجودی پنل اس ام اس
    const checkBalance = async () => {
        setBalanceLoading(true);
        try {
            const response = await axios.post("/api/get_sms_balance.php", {
                token: localStorage.getItem("adminlogintoken")
            });

            if (response.status === 200 && response.data.error === false) {
                setBalance(response.data.sms_count);
                setBalanceDetails({
                    rial: response.data.balance_rial,
                    toman: response.data.balance_toman,
                    sms_price: response.data.sms_price_toman,
                    formatted_sms: response.data.formatted_sms,
                    formatted_rial: response.data.formatted_rial,
                    formatted_toman: response.data.formatted_toman
                });
                console.log("اطلاعات موجودی:", response.data);
            } else {
                setErrorMsg("خطا در دریافت موجودی");
            }
        } catch (error) {
            console.error("Error checking balance:", error);
            setErrorMsg("خطا در ارتباط با سرور");
        } finally {
            setBalanceLoading(false);
        }
    };

    // انتخاب/لغو انتخاب همه
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedCustomers([]);
        } else {
            setSelectedCustomers(filteredList.map(c => c.id));
        }
        setSelectAll(!selectAll);
    };

    // انتخاب/لغو انتخاب یک مشتری
    const handleSelectCustomer = (customerId) => {
        if (selectedCustomers.includes(customerId)) {
            setSelectedCustomers(selectedCustomers.filter(id => id !== customerId));
            setSelectAll(false);
        } else {
            setSelectedCustomers([...selectedCustomers, customerId]);
            if (selectedCustomers.length + 1 === filteredList.length) {
                setSelectAll(true);
            }
        }
    };

    // پاک کردن انتخاب‌ها
    const handleClearSelection = () => {
        setSelectedCustomers([]);
        setSelectAll(false);
    };

    // اعمال قالب پیامک
    const applyTemplate = (templateText) => {
        setMessage(templateText);
    };

    // ارسال پیامک به مشتریان انتخاب شده
    const handleSendSMS = async () => {
        if (selectedCustomers.length === 0) {
            setErrorMsg("لطفا حداقل یک مشتری را انتخاب کنید");
            return;
        }

        if (message.trim() === "") {
            setErrorMsg("لطفا متن پیامک را وارد کنید");
            return;
        }

        setLoading(true);
        try {
            const selectedCustomerData = customerList.filter(c => selectedCustomers.includes(c.id));
            const phones = selectedCustomerData.map(c => c.phone).filter(p => p && p.length > 10);

            if (phones.length === 0) {
                setErrorMsg("هیچ شماره تماس معتبری برای ارسال وجود ندارد");
                setLoading(false);
                return;
            }

            const response = await axios.post("/api/send_bulk_sms.php", {
                token: localStorage.getItem("adminlogintoken"),
                phones: phones,
                message: message
            });

            if (response.data.error === false) {
                setSuccsMsg(response.data.message);
                setMessage("");
                checkBalance();
            } else {
                setErrorMsg(response.data.message);
            }

        } catch (error) {
            console.error("Error sending SMS:", error);
            setErrorMsg("خطا در ارسال پیامک: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    // ذخیره قالب پیامک
    const handleSaveTemplate = async () => {
        if (!editingTemplate.title || !editingTemplate.content) {
            setErrorMsg("عنوان و متن قالب الزامی است");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post("/api/save_sms_template.php", {
                token: localStorage.getItem("adminlogintoken"),
                id: editingTemplate.id,
                title: editingTemplate.title,
                content: editingTemplate.content
            });

            if (response.data.error === false) {
                setSuccsMsg(response.data.message);
                setTemplateDialogOpen(false);
                setEditingTemplate({ id: 0, title: '', content: '' });
                await fetchTemplates();
            } else {
                setErrorMsg(response.data.message);
            }
        } catch (error) {
            console.error("Error saving template:", error);
            setErrorMsg("خطا در ذخیره قالب: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    // حذف قالب پیامک
    const handleDeleteTemplate = async (id) => {
        if (!window.confirm("آیا از حذف این قالب اطمینان دارید؟")) return;

        setLoading(true);
        try {
            const response = await axios.post("/api/delete_sms_template.php", {
                token: localStorage.getItem("adminlogintoken"),
                id: id
            });

            if (response.data.error === false) {
                setSuccsMsg(response.data.message);
                await fetchTemplates();
            } else {
                setErrorMsg(response.data.message);
            }
        } catch (error) {
            console.error("Error deleting template:", error);
            setErrorMsg("خطا در حذف قالب: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const Redirect = () => {
        return (<Navigate replace to="/login" />);
    };

    return (
        <div>
            {localStorage.getItem("adminlogintoken") === "" ? <Redirect /> : ''}
            {localStorage.getItem("adminlogintoken") === null ? <Redirect /> : ''}
            {errorMsg !== '' ? <ErrorMsg message={errorMsg} openn={true} seterrormsg={setErrorMsg} /> : ''}
            {succsMsg !== '' ? <SuccsMsg message={succsMsg} openn={true} setsuccsmsg={setSuccsMsg} /> : ''}

            <Grid container spacing={2} sx={{ marginTop: 0 }}>
                <Grid item xs={12} md={10} sx={{ backgroundColor: "#ffffff", minHeight: "100vh" }}>
                    <Appbar />

                    <Box sx={{ padding: 3 }} dir={"rtl"}>
                        {/* کارت نمایش موجودی */}
                        <BalanceCard>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <AccountBalanceWalletIcon sx={{ fontSize: 40, ml: 2 }} />
                                        <Box>
                                            <Typography variant="h6" component="div">
                                                موجودی پنل پیامک
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                قیمت هر پیامک: ۱۷۰ تومان
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ textAlign: 'left', ml: 2 }}>
                                            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', direction: 'ltr' }}>
                                                {balanceLoading ? <CircularProgress size={30} color="inherit" /> :
                                                    (balance !== null ? formatNumber(balance) : '۰')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                تعداد پیامک قابل ارسال
                                            </Typography>
                                            {balanceDetails && (
                                                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                                                    {balanceDetails.formatted_toman}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Tooltip title="بروزرسانی موجودی">
                                            <IconButton onClick={checkBalance} color="inherit" sx={{ mr: 1 }}>
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </CardContent>
                        </BalanceCard>

                        {/* تب‌های مختلف */}
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label="ارسال گروهی" icon={<MessageIcon />} iconPosition="start" />
                            <Tab label="مدیریت قالب‌ها" icon={<EditIcon />} iconPosition="start" />
                        </Tabs>

                        {/* تب ارسال گروهی */}
                        {activeTab === 0 && (
                            <Grid container spacing={3}>
                                {/* بخش انتخاب مشتریان */}
                                <Grid item xs={12} md={7}>
                                    <Paper sx={{ p: 2, mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                            <Typography variant="h6">
                                                لیست مشتریان
                                            </Typography>
                                            <Box>
                                                <Chip
                                                    label={`${selectedCustomers.length} انتخاب شده`}
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ ml: 1 }}
                                                />
                                                {selectedCustomers.length > 0 && (
                                                    <Button
                                                        size="small"
                                                        onClick={handleClearSelection}
                                                        color="error"
                                                        startIcon={<DeleteIcon />}
                                                    >
                                                        پاک کردن
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* فیلتر جستجو */}
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="جستجوی نام یا شماره تماس..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            sx={{ mb: 2 }}
                                            InputProps={{
                                                startAdornment: <SearchIcon sx={{ ml: 1, color: 'gray' }} />,
                                            }}
                                        />

                                        {/* جدول مشتریان */}
                                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <StyledTableCell align="center" width={50}>
                                                            <Checkbox
                                                                checked={selectAll}
                                                                onChange={handleSelectAll}
                                                                sx={{ color: 'white' }}
                                                            />
                                                        </StyledTableCell>
                                                        <StyledTableCell align="right">نام مشتری</StyledTableCell>
                                                        <StyledTableCell align="right">شماره تماس</StyledTableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredList.length > 0 ? (
                                                        filteredList.map((customer) => (
                                                            <StyledTableRow key={customer.id}>
                                                                <StyledTableCell align="center">
                                                                    <Checkbox
                                                                        checked={selectedCustomers.includes(customer.id)}
                                                                        onChange={() => handleSelectCustomer(customer.id)}
                                                                    />
                                                                </StyledTableCell>
                                                                <StyledTableCell align="right">{customer.name}</StyledTableCell>
                                                                <StyledTableCell align="right" dir="ltr">{customer.phone}</StyledTableCell>
                                                            </StyledTableRow>
                                                        ))
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={3} align="center">
                                                                {searchTerm ? "مشتری‌ای یافت نشد" : "لیست مشتریان خالی است"}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </Grid>

                                {/* بخش نوشتن پیامک */}
                                <Grid item xs={12} md={5}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2 }}>
                                            متن پیامک
                                        </Typography>

                                        {/* انتخاب قالب */}
                                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                            <InputLabel>قالب پیامک</InputLabel>
                                            <Select
                                                value={selectedTemplate}
                                                label="قالب پیامک"
                                                onChange={(e) => {
                                                    setSelectedTemplate(e.target.value);
                                                    if (e.target.value) {
                                                        const template = templates.find(t => t.id === e.target.value);
                                                        if (template) applyTemplate(template.content);
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">انتخاب قالب</MenuItem>
                                                {templates.map(template => (
                                                    <MenuItem key={template.id} value={template.id}>
                                                        {template.title}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <TextField
                                            fullWidth
                                            label="متن پیامک"
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            multiline
                                            rows={8}
                                            sx={{ mb: 2 }}
                                            placeholder="متن پیامک خود را وارد کنید..."
                                        />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" color="textSecondary">
                                                تعداد کاراکتر: {message.length} | تعداد پیامک: {Math.ceil(message.length / 160)}
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                endIcon={<SendIcon />}
                                                onClick={handleSendSMS}
                                                disabled={selectedCustomers.length === 0 || !message}
                                            >
                                                ارسال به {selectedCustomers.length} نفر
                                            </Button>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}

                        {/* تب مدیریت قالب‌ها */}
                        {activeTab === 1 && (
                            <Paper sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h6">
                                        مدیریت قالب‌های پیامک
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            setEditingTemplate({ id: 0, title: '', content: '' });
                                            setTemplateDialogOpen(true);
                                        }}
                                    >
                                        قالب جدید
                                    </Button>
                                </Box>

                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell align="right">عنوان قالب</StyledTableCell>
                                                <StyledTableCell align="right">متن قالب</StyledTableCell>
                                                <StyledTableCell align="center">عملیات</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {templates.length > 0 ? (
                                                templates.map((template) => (
                                                    <StyledTableRow key={template.id}>
                                                        <StyledTableCell align="right">{template.title}</StyledTableCell>
                                                        <StyledTableCell align="right" sx={{ maxWidth: 300 }}>
                                                            <Typography noWrap>
                                                                {template.content}
                                                            </Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell align="center">
                                                            <IconButton
                                                                color="primary"
                                                                onClick={() => {
                                                                    setEditingTemplate(template);
                                                                    setTemplateDialogOpen(true);
                                                                }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                color="error"
                                                                onClick={() => handleDeleteTemplate(template.id)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center">
                                                        هیچ قالبی یافت نشد
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={0} md={2} sx={{
                    backgroundColor: '#2b2b2b',
                    minHeight: "100vh",
                    display: { xs: "none", md: "block" }
                }}>
                    <MenuList />
                </Grid>
            </Grid>

            {/* دیالوگ افزودن/ویرایش قالب */}
            <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingTemplate.id ? "ویرایش قالب" : "افزودن قالب جدید"}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="عنوان قالب"
                        value={editingTemplate.title}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, title: e.target.value })}
                        margin="normal"
                        sx={{ mt: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="متن قالب"
                        value={editingTemplate.content}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, content: e.target.value })}
                        multiline
                        rows={6}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTemplateDialogOpen(false)} startIcon={<CancelIcon />}>
                        انصراف
                    </Button>
                    <Button onClick={handleSaveTemplate} variant="contained" color="primary" startIcon={<SaveIcon />}>
                        ذخیره
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={loading || pageLoading}>
                <CircularProgress sx={{ margin: 2 }} />
            </Dialog>
        </div>
    );
};

export default SendSMS;